import { useCallback, useEffect, useMemo } from 'react';
import { extractVariablesFromMultiple, getMissingVariables, isProtectedSystemVariableKey } from '../utils/variableExtractor';
import {
  normalizeAgentDynamicVariableDefinition,
  normalizeAgentDynamicVariables,
  type AgentDynamicVariableValue,
} from '../utils/agentDynamicVariables';

export interface DynamicVariablesContainer {
  dynamicVariables?: Record<string, AgentDynamicVariableValue>;
}

interface UseDynamicVariablesProps<T extends DynamicVariablesContainer> {
  config: T;
  onChange: (updatedConfig: T) => void;
  sourceTexts?: (string | undefined | null)[];
}

/**
 * Track detected script variables and keep dynamic variables in sync.
 */
export function useDynamicVariables<T extends DynamicVariablesContainer>({
  config,
  onChange,
  sourceTexts = [],
}: UseDynamicVariablesProps<T>) {

  const normalizedSourceTexts = useMemo(
    () => sourceTexts.filter((text): text is string => Boolean(text)),
    [sourceTexts],
  );

  const hasSourceContent = normalizedSourceTexts.length > 0;
  const extractedVariables = useMemo(
    () => extractVariablesFromMultiple(normalizedSourceTexts).filter((variable) => !isProtectedSystemVariableKey(variable)),
    [normalizedSourceTexts],
  );

  const normalizedDynamicVariables = useMemo(
    () => normalizeAgentDynamicVariables(config.dynamicVariables),
    [config.dynamicVariables],
  );

  const manualVariables = useMemo(
    () => Object.keys(normalizedDynamicVariables),
    [normalizedDynamicVariables],
  );

  const detectedVariables = hasSourceContent ? extractedVariables : manualVariables;

  const missingVariables = useMemo(
    () =>
      hasSourceContent ? getMissingVariables(detectedVariables, normalizedDynamicVariables) : [],
    [detectedVariables, normalizedDynamicVariables, hasSourceContent],
  );

  const updateDynamicVariable = useCallback(
    (name: string, value: string) => {
      const newDynamicVariables = {
        ...normalizedDynamicVariables,
        [name]: {
          ...normalizeAgentDynamicVariableDefinition(normalizedDynamicVariables[name]),
          value,
        },
      };
      onChange({ ...config, dynamicVariables: newDynamicVariables });
    },
    [config, normalizedDynamicVariables, onChange],
  );

  useEffect(() => {
    if (!hasSourceContent || detectedVariables.length === 0) return;
    const currentKeys = Object.keys(normalizedDynamicVariables);
    const orphanedKeys = currentKeys.filter((key) => !detectedVariables.includes(key));
    if (orphanedKeys.length === 0) return;
    const cleanedVariables = { ...normalizedDynamicVariables };
    orphanedKeys.forEach((key) => delete cleanedVariables[key]);
    onChange({ ...config, dynamicVariables: cleanedVariables });
  }, [config, detectedVariables, hasSourceContent, normalizedDynamicVariables, onChange]);

  return {
    detectedVariables,
    missingVariables,
    dynamicVariables: normalizedDynamicVariables,
    updateDynamicVariable,
  };
}
